<script setup lang="ts">
import type { AxiosProgressEvent } from 'axios'
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import CheckBoxDefault from '@/components/forms/Checkboxes/CheckBoxDefault.vue'
import { notificationService } from '@/services/notificationService'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import {
  EditIcon, DownloadIcon, EyeIcon, MergeIcon, DeleteIcon,
  FilePdfIcon, FileExcleIcon, FileWordIcon, FileJpgIcon, FilePngIcon, FileWaveIcon,
} from '@/icons'
import EmptyState from '@/components/common/EmptyState.vue'
import DocsIcon from '@/icons/DocsIcon.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import TextAreaDefault from '@/components/forms/TextArea/TextAreaDefault.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import html2canvas from 'html2canvas'
import mediaService from '@/services/mediaService'
import { PDFDocument, type PDFPage } from 'pdf-lib'
import type { MediaDocument } from '@/types/media'

import {
  TAUTemplateGenerator,
  BlackListingTemplateGenerator,
  MakeInIndiaTemplateGenerator,
  MinimumWagePolicyTemplateGenerator,
  LandBorderSharingDeclarationTemplateGenerator,
  ConfirmationOfNoRelativeTemplateGenerator,
  PowerOfAttorneyTemplateGenerator,
  BidSecurityDeclarationTemplateGenerator,
  NoPendingLitigationDeclarationTemplateGenerator,
  NonBankruptcyDeclarationTemplateGenerator,
  NonLiquidationDeclarationTemplateGenerator,
  NonDisclosureDeclarationTemplateGenerator,
} from '@/utils/templateGenerator'

import type { CompanyType } from '@/types/user'
import GlobalFormMessage from '@/components/forms/GlobalFormMessage.vue'

const downloadingFileId = ref<string | null>(null)

const MAX_FILE_SIZE = 50 * 1024 * 1024
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE / 1024 / 1024

const changesSaved = ref(false)

// ─── Types ────────────────────────────────────────────────────────────────────

interface TemplateGeneratorClass { [key: string]: (data: TemplateData) => string }

interface AxiosErrorType {
  response?: { status?: number; data?: { message?: string; error?: string } }
  code?: string
  message?: string
}

interface CompanyData {
  name: string; address: string; authorizedPerson: string; positionPower: string
  signatoryName: string; designation: string; minimumLocalContent: string; locationAddition: string
}

interface TemplateData {
  companySignatoryName: string; companyAddress: string; companyLogo: string; companySignatory: string
  organization: string; departmentAddress: string; tenderRefNo: string; date: string
  tenderBrief: string; locationAddition: string; minimumLocalContent: string; signatoryName: string
  designation: string; signatureDate: string; authorizedPerson: string; position: string
  departmentName: string; [key: string]: string | number | boolean
}

interface UploadedFileInfo {
  filename: string; originalName?: string; url: string
  size?: number; uploadedAt?: string; selectedDocuments?: string[]
  selectedSharedDocs?: string[]
}

interface TenderData {
  referenceNumber?: string; referenceNo?: string; organization?: string
  departmentName?: string; tenderBrief?: string; departmentAddress?: string
  [key: string]: string | number | boolean | undefined
}

interface MediaDocumentItem {
  id: string; title: string; fileType: string; fileSize: string | number
  description?: string; uploadedAt: string; uploadedBy: string; selected?: boolean
}

const tempSelectedSharedDocIds = ref<string[]>([])

// ─── Props / emits ────────────────────────────────────────────────────────────

const props = defineProps<{
  tenderId: string
  tenderStatus?: string
  tenderData?: TenderData | null
  userCompany?: CompanyType
}>()

const emit = defineEmits<{ (e: 'checklist-updated'): void }>()

const authStore = useAuthStore()

// ─── upload progressbar ─────────────────────────────────────────────────
const uploadProgress = ref(0)

// ─── Delete document ─────────────────────────────────────────────────

const showDeleteModal = ref(false)
const deletingDocument = ref(false)
const documentToDelete = ref<UploadedFileInfo | null>(null)
const fileToDeleteIndex = ref<number | null>(null)

// const deleteModalTitle = computed(() => {
//   return `Delete "${documentToDelete.value?.originalName || documentToDelete.value?.filename || 'Document'}"?`
// })

// Open delete confirmation modal
const confirmDeleteFile = (index: number) => {
  const file = uploadedFiles.value[index]
  if (!file) return
  documentToDelete.value = file
  fileToDeleteIndex.value = index
  showDeleteModal.value = true
}

// Close delete modal
const closeDeleteModal = () => {
  showDeleteModal.value = false
  documentToDelete.value = null
  fileToDeleteIndex.value = null
}

// Handle delete submission
const handleDeleteSubmit = async () => {
  if (fileToDeleteIndex.value === null) return

  const file = uploadedFiles.value[fileToDeleteIndex.value]
  if (!file) return

  deletingDocument.value = true

  try {
    await api.delete(`/api/tenders/${props.tenderId}/compliance-file`, {
      data: { filename: file.filename }
    })
    uploadedFiles.value.splice(fileToDeleteIndex.value, 1)
    uploadedFile.value = uploadedFiles.value[0] ?? null
    notificationService.success('File deleted successfully')
    closeDeleteModal()
  } catch (e) {
    notificationService.error('Failed to delete file')
    console.error(e)
  } finally {
    deletingDocument.value = false
  }
}

// ─── Checklist document names ─────────────────────────────────────────────────

const checklistDocuments: string[] = [
  'Tender Acceptance Undertaking',
  'Blacklisting / Non - Debarment',
  'Make in India',
  'Minimum Wage Policy',
  'Land Border Sharing Declaration',
  'Confirmation of No Relative',
  'Power of Attorney',
  'Bid Security Declaration',
  'No Pending Litigation Declaration',
  'Non Bankruptcy Declaration',
  'Non Liquidation Declaration',
  'Non Disclosure Declaration',
]

// ─── State ────────────────────────────────────────────────────────────────────

const showModal = ref(false)
const checkedTemplates = ref<Record<string, boolean>>({})
const selectedSharedDocIds = ref<string[]>([])
const loading = ref(false)
const generatingPDF = ref(false)
const generationStep = ref('')
const uploadedFile = ref<UploadedFileInfo | null>(null)   // legacy — points to latest
const uploadedFiles = ref<UploadedFileInfo[]>([])          // full list, newest first
const deletingFileIndex = ref<number | null>(null)
const loadingFile = ref(false)
const departmentAddress = ref('')
const departmentAddressError = ref('')

const companyLogoBase64 = ref('')
const companySignatureBase64 = ref('')
const companyData = ref<CompanyData>({
  name: '', address: '', authorizedPerson: '', positionPower: '',
  signatoryName: '', designation: '', minimumLocalContent: '', locationAddition: '',
})

const sharedDocuments = ref<MediaDocumentItem[]>([])
const selectedSharedDocs = ref<MediaDocumentItem[]>([])
const docsLoading = ref(false)
const docsError = ref<string | null>(null)
const searchFilter = ref('')

checklistDocuments.forEach(doc => { checkedTemplates.value[doc] = false })

// ─── Computed ─────────────────────────────────────────────────────────────────

const isAgent = computed(() => authStore.user?.userType === 'agent')
const isTenderWonOrLost = computed(() =>
  ['won', 'lost', 'assigned-to-engineer', 'completed'].includes(props.tenderStatus || '')
)

const selectedChecklistDocs = computed(() => checklistDocuments.filter(d => checkedTemplates.value[d]))
const hasSelectedChecklistDocs = computed(() => selectedChecklistDocs.value.length > 0)
const checkedList = computed(() => selectedChecklistDocs.value)

// const allSharedSelected = computed({
//   get: () => sharedDocuments.value.length > 0 && sharedDocuments.value.every(d => d.selected === true),
//   set: async (value: boolean) => {
//     sharedDocuments.value.forEach(d => {
//       if (isMergeableFile(d)) d.selected = value
//     })
//     // Update temp selection instead of direct DB save
//     if (value) {
//       tempSelectedSharedDocIds.value = sharedDocuments.value
//         .filter(d => isMergeableFile(d))
//         .map(d => d.id)
//     } else {
//       tempSelectedSharedDocIds.value = []
//     }
//     syncSelectedSharedDocs()
//     // DO NOT save to DB yet
//   },
// })

const mergeableSelectedDocs = computed(() => selectedSharedDocs.value.filter(d => isMergeableFile(d)))
const hasUnmergeableFiles = computed(() => selectedSharedDocs.value.some(d => !isMergeableFile(d)))
const unmergeableFileNames = computed(() =>
  selectedSharedDocs.value.filter(d => !isMergeableFile(d)).map(d => d.title).join(', ')
)

const canGenerate = computed(() =>
  (hasSelectedChecklistDocs.value || mergeableSelectedDocs.value.length > 0) && !generatingPDF.value
)

const generateButtonText = computed(() => {
  if (generatingPDF.value) return generationStep.value || 'Generating…'
  const c = selectedChecklistDocs.value.length
  const d = mergeableSelectedDocs.value.length
  if (c === 0 && d === 0) return 'Select documents'
  const parts: string[] = []
  if (c > 0) parts.push(`${c} compliance`)
  if (d > 0) parts.push(`${d} shared`)
  return `Generate Merged PDF (${parts.join(' + ')})`
})

// ─── File-type helpers ────────────────────────────────────────────────────────

const isMergeableFile = (doc: MediaDocumentItem): boolean => {
  const type = doc.fileType.toLowerCase()
  const title = doc.title.toLowerCase()
  if (type.includes('pdf') || title.includes('.pdf') || title.endsWith('pdf')) return true
  if (['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'image/'].some(t => type.includes(t))) return true
  return ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff']
    .some(ext => title.includes(ext) || title.endsWith(ext.replace('.', '')))
}

const isOfficeDocument = (doc: MediaDocumentItem): boolean =>
  ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'].some(ext => doc.title.toLowerCase().endsWith(ext))

const getDocumentIcon = (doc: MediaDocumentItem) => {
  const type = doc.fileType.toLowerCase(); const title = doc.title.toLowerCase()
  if (type.includes('pdf') || title.endsWith('.pdf')) return FilePdfIcon
  if (type.includes('excel') || title.includes('.xls')) return FileExcleIcon
  if (type.includes('word') || title.includes('.doc')) return FileWordIcon
  if (type.includes('jpeg') || type.includes('jpg') || title.endsWith('.jpg') || title.endsWith('.jpeg')) return FileJpgIcon
  if (type.includes('png') || title.endsWith('.png')) return FilePngIcon
  return FileWaveIcon
}

// ─── Company data ─────────────────────────────────────────────────────────────

const companyDataMap: Record<string, CompanyData> = {
  maxstationary: {
    name: 'Max Stationary', address: '123 Business Park, Mumbai - 400001',
    authorizedPerson: 'Rajesh Kumar', positionPower: 'Director',
    signatoryName: 'Rajesh Kumar', designation: 'Director',
    minimumLocalContent: '35', locationAddition: 'Mumbai',
  },
  ostnasik: {
    name: 'OST Nasik', address: '456 Industrial Area, Nashik - 422001',
    authorizedPerson: 'Priya Sharma', positionPower: 'Branch Manager',
    signatoryName: 'Priya Sharma', designation: 'Branch Manager',
    minimumLocalContent: '28', locationAddition: 'Nashik',
  },
  'shradhha-ent': {
    name: 'SHRADDHA ENTERPRISES',
    address: 'Preeti Pushp , Plot No 12, Kartikiya Nagar, Kamatwade, Ambad , Nashik -422010',
    authorizedPerson: 'SHRADDHA R SHIRSAT', positionPower: 'PROPERITOR',
    signatoryName: 'SHRADDHA RAJU SHIRSAT', designation: 'PROPERITOR',
    minimumLocalContent: '21', locationAddition: 'Nashik',
  },
}

const companyAssetsMap: Record<string, { logo: string; signature: string }> = {
  maxstationary: { logo: '/images/companies/maxstationary/logo.png', signature: '/images/companies/maxstationary/signature.png' },
  ostnasik: { logo: '/images/companies/ostnasik/logo.png', signature: '/images/companies/ostnasik/signature.png' },
  'shradhha-ent': { logo: '/images/companies/shraddha-enterprises/logo.png', signature: '/images/companies/shraddha-enterprises/signature.png' },
}

const fetchImageAsBase64 = async (path: string): Promise<string> => {
  try {
    const blob = await (await fetch(path)).blob()
    return new Promise((res, rej) => {
      const r = new FileReader(); r.readAsDataURL(blob)
      r.onload = () => res(r.result as string); r.onerror = rej
    })
  } catch { return '' }
}

const loadCompanyData = async () => {
  const company = props.userCompany; if (!company) return
  const data = companyDataMap[company]; if (data) companyData.value = { ...data }
  const assets = companyAssetsMap[company]
  if (assets) {
    companyLogoBase64.value = assets.logo ? await fetchImageAsBase64(assets.logo).catch(() => '') : ''
    companySignatureBase64.value = assets.signature ? await fetchImageAsBase64(assets.signature).catch(() => '') : ''
  }
}

const formatDateForPDF = (ds: string): string => {
  if (!ds) return ''
  try {
    const d = new Date(ds)
    if (isNaN(d.getTime())) return ds
    return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`
  } catch { return ds }
}

// ─── Compliance API ───────────────────────────────────────────────────────────

const loadChecklistData = async () => {
  if (!props.tenderId) return
  try {
    loading.value = true
    const res = await api.get(`/api/tenders/${props.tenderId}/compliance-checklist`, {
      params: { _: Date.now() } // Cache busting
    })
    const raw = res.data?.data?.checklist

    console.log('📥 LOADED FROM DB - Raw data:', JSON.stringify(raw, null, 2))

    if (raw) {
      if (typeof raw === 'string') {
        const parsed = JSON.parse(raw)
        if (parsed.templates && parsed.sharedDocs) {
          checkedTemplates.value = parsed.templates
          selectedSharedDocIds.value = parsed.sharedDocs || []
        } else {
          checkedTemplates.value = parsed
          selectedSharedDocIds.value = []
        }
      } else {
        checkedTemplates.value = raw.templates || {}
        selectedSharedDocIds.value = raw.sharedDocs || []
      }
    } else {
      selectedSharedDocIds.value = []
    }

    console.log('✅ selectedSharedDocIds after load:', selectedSharedDocIds.value)
    await fetchSharedDocuments()
  } catch (e) {
    console.error('Checklist load error:', e)
  } finally {
    loading.value = false
  }
}

const loadUploadedFile = async () => {
  if (!props.tenderId) return
  try {
    loadingFile.value = true
    uploadedFile.value = null
    uploadedFiles.value = []
    const res = await api.get(`/api/tenders/${props.tenderId}/compliance-file`, {
      params: { _: Date.now() }
    })
    const data = res.data?.data
    if (!data) return
    // Server returns an array (new) or single object (legacy)
    if (Array.isArray(data)) {
      uploadedFiles.value = data
      uploadedFile.value = data[0] ?? null
    } else if (data && data.filename) {
      uploadedFiles.value = [data]
      uploadedFile.value = data
    }
    console.log('📁 Loaded', uploadedFiles.value.length, 'compliance file(s)')
  } catch (e) {
    console.error('Error loading uploaded file:', e)
  } finally {
    loadingFile.value = false
  }
}

const saveChecklistToDB = async () => {
  if (!props.tenderId) {
    console.error('❌ No tenderId provided!')
    return
  }
  const saveData = {
    templates: checkedTemplates.value,
    sharedDocs: selectedSharedDocIds.value
  }
  console.log('💾 SAVING TO DATABASE:', JSON.stringify(saveData, null, 2))
  console.log('📊 sharedDocs count:', selectedSharedDocIds.value.length)
  try {
    const response = await api.put(`/api/tenders/${props.tenderId}/compliance-checklist`, {
      checklist: saveData
    })
    console.log('✅ Database save response:', response.data)
  } catch (error) {
    console.error('❌ Failed to save checklist:', error)
  }
}

// ─── Shared Documents API ─────────────────────────────────────────────────────

// Add a new ref for filtered documents
const filteredSharedDocuments = ref<MediaDocumentItem[]>([])

// Update fetchSharedDocuments to load all documents without filtering
const fetchSharedDocuments = async () => {
  try {
    docsLoading.value = true
    docsError.value = null
    const all = await mediaService.getAllDocuments({})

    console.log('📄 Fetched documents count:', all.length)

    sharedDocuments.value = all.map((doc: MediaDocument) => {
      // Use tempSelectedSharedDocIds for display
      const isSelected = tempSelectedSharedDocIds.value.includes(doc.id)
      const isMergeableVal = isMergeableFile({
        id: doc.id, title: doc.title || 'Untitled', fileType: doc.fileType || 'unknown',
        fileSize: doc.fileSize || 0, uploadedAt: doc.uploadedAt || '', uploadedBy: ''
      })

      return {
        id: doc.id, title: doc.title || 'Untitled', fileType: doc.fileType || 'unknown',
        fileSize: doc.fileSize || 0, description: doc.description || undefined,
        uploadedAt: doc.uploadedAt || new Date().toISOString(),
        uploadedBy: doc.uploadedBy || 'Unknown',
        selected: isSelected && isMergeableVal
      }
    })

    // Apply initial filter
    updateFilteredDocuments()

    selectedSharedDocs.value = sharedDocuments.value.filter(d => d.selected === true)
    console.log('📊 selectedSharedDocs count:', selectedSharedDocs.value.length)
  } catch (err: unknown) {
    docsError.value = err instanceof Error ? err.message : 'Failed to load documents'
    notificationService.error('Failed to load documents')
    sharedDocuments.value = []
  } finally {
    docsLoading.value = false
  }
}

// New function to filter documents without losing selection
const updateFilteredDocuments = () => {
  if (!searchFilter.value.trim()) {
    filteredSharedDocuments.value = [...sharedDocuments.value]
  } else {
    const searchTerm = searchFilter.value.toLowerCase()
    filteredSharedDocuments.value = sharedDocuments.value.filter(doc =>
      doc.title.toLowerCase().includes(searchTerm) ||
      (doc.description && doc.description.toLowerCase().includes(searchTerm))
    )
  }
}

const handleSharedDocCheckbox = async (doc: MediaDocumentItem, checked: boolean) => {
  doc.selected = checked

  if (checked) {
    if (!tempSelectedSharedDocIds.value.includes(doc.id)) {
      tempSelectedSharedDocIds.value = [...tempSelectedSharedDocIds.value, doc.id]
    }
  } else {
    tempSelectedSharedDocIds.value = tempSelectedSharedDocIds.value.filter(id => id !== doc.id)
  }

  // Update the filtered view for immediate UI feedback
  syncSelectedSharedDocs()

  // DO NOT save to DB yet - wait until successful merge
  // await saveChecklistToDB()
}

const syncSelectedSharedDocs = () => {
  selectedSharedDocs.value = sharedDocuments.value.filter(d => tempSelectedSharedDocIds.value.includes(d.id))
}

const viewDocument = async (doc: MediaDocumentItem) => {
  try {
    await mediaService.viewDocument(doc.id)
  } catch (err: unknown) {
    console.error('Error viewing document:', err)
    const errorMessage = err instanceof Error ? err.message : 'Failed to view document'
    notificationService.error(errorMessage)
  }
}

let searchTimeout: number

const handleSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = window.setTimeout(() => {
    updateFilteredDocuments()
  }, 500)
}

const clearSearch = () => {
  searchFilter.value = ''
  updateFilteredDocuments()
}
// ─── Modal ────────────────────────────────────────────────────────────────────

const openModal = () => {
  departmentAddressError.value = ''
  // Reset all selections when opening modal
  tempSelectedSharedDocIds.value = []  // Reset Shared Documents checkboxes
  changesSaved.value = false

  // Reset Compliance Documents checkboxes
  checkedTemplates.value = {}
  checklistDocuments.forEach(doc => {
    checkedTemplates.value[doc] = false
  })

  // Update sharedDocuments to reflect deselected state
  sharedDocuments.value = sharedDocuments.value.map(doc => ({
    ...doc,
    selected: false
  }))

  // Update filtered view as well
  updateFilteredDocuments()

  showModal.value = true
  if (sharedDocuments.value.length === 0) fetchSharedDocuments()
}

const closeModal = () => {
  showModal.value = false
  departmentAddress.value = ''
  departmentAddressError.value = ''
  // Discard unsaved changes - reset temp selection
  tempSelectedSharedDocIds.value = [...selectedSharedDocIds.value]
  // Refresh the shared documents view to reflect saved state
  fetchSharedDocuments()
}

// ─── Template HTML builder ────────────────────────────────────────────────────

const buildTemplateHtml = (docName: string, data: TemplateData): string => {
  const g = (cls: unknown) => cls as unknown as TemplateGeneratorClass
  switch (docName) {
    case 'Tender Acceptance Undertaking':     return g(TAUTemplateGenerator)['createTenderAcceptanceHTML'](data)
    case 'Blacklisting / Non - Debarment':    return g(BlackListingTemplateGenerator)['createBlacklistingHTML'](data)
    case 'Make in India':                     return g(MakeInIndiaTemplateGenerator)['createMakeInIndiaHTML'](data)
    case 'Minimum Wage Policy':               return g(MinimumWagePolicyTemplateGenerator)['createMinimumWagePolicyHTML'](data)
    case 'Land Border Sharing Declaration':   return g(LandBorderSharingDeclarationTemplateGenerator)['createLandBorderSharingDeclarationHTML'](data)
    case 'Confirmation of No Relative':       return g(ConfirmationOfNoRelativeTemplateGenerator)['createConfirmationOfNoRelativeHTML'](data)
    case 'Power of Attorney':                 return g(PowerOfAttorneyTemplateGenerator)['createPowerOfAttorneyHTML'](data)
    case 'Bid Security Declaration':          return g(BidSecurityDeclarationTemplateGenerator)['createBidSecurityDeclarationHTML'](data)
    case 'No Pending Litigation Declaration': return g(NoPendingLitigationDeclarationTemplateGenerator)['createNoPendingLitigationDeclarationHTML'](data)
    case 'Non Bankruptcy Declaration':        return g(NonBankruptcyDeclarationTemplateGenerator)['createNonBankruptcyDeclarationHTML'](data)
    case 'Non Liquidation Declaration':       return g(NonLiquidationDeclarationTemplateGenerator)['createNonLiquidationDeclarationHTML'](data)
    case 'Non Disclosure Declaration':        return g(NonDisclosureDeclarationTemplateGenerator)['createNonDisclosureDeclarationHTML'](data)
    default: return ''
  }
}

// ─── Canvas renderer ──────────────────────────────────────────────────────────

const renderHtmlToCanvas = async (fullHtml: string): Promise<HTMLCanvasElement> => {
  const container = document.createElement('div')
  container.innerHTML = fullHtml
  container.style.cssText =
    'width:800px;padding:40px;background:#fff;font-family:Times New Roman,serif;' +
    'position:fixed;top:0;left:0;z-index:10000;visibility:visible'
  document.body.appendChild(container)
  try {
    await new Promise(r => setTimeout(r, 300))
    const canvas = await html2canvas(container, {
      scale: 1.5, backgroundColor: '#ffffff', logging: false, allowTaint: true, useCORS: true,
    })
    document.body.removeChild(container)
    return canvas
  } catch (err) {
    if (document.body.contains(container)) document.body.removeChild(container)
    throw err
  }
}

type JsPDFInstance = InstanceType<Awaited<typeof import('jspdf')>['default']>
const addCanvasToPdf = (pdf: JsPDFInstance, canvas: HTMLCanvasElement, firstPage: boolean) => {
  const pdfW = pdf.internal.pageSize.getWidth()
  const pdfH = pdf.internal.pageSize.getHeight()
  const imgH = (canvas.height / canvas.width) * pdfW
  if (!firstPage) pdf.addPage()
  if (imgH <= pdfH) {
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pdfW, imgH)
  } else {
    const pixelsPerPage = Math.floor((canvas.width / pdfW) * pdfH)
    const totalSlices = Math.ceil(canvas.height / pixelsPerPage)
    for (let s = 0; s < totalSlices; s++) {
      if (s > 0) pdf.addPage()
      const srcY = s * pixelsPerPage
      const srcH = Math.min(pixelsPerPage, canvas.height - srcY)
      const slice = document.createElement('canvas')
      slice.width = canvas.width; slice.height = srcH
      slice.getContext('2d')!.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH)
      pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pdfW, (srcH / canvas.width) * pdfW)
    }
  }
}

// Add these refs near other state variables (around line ~100)
const downloadProgress = ref<{ current: number; total: number; currentFile: string }>({
  current: 0,
  total: 0,
  currentFile: ''
})

// Add retry function before generateMergedPDF
const downloadWithRetry = async (docId: string, retries = 2, delay = 3000): Promise<Blob> => {
  for (let i = 0; i <= retries; i++) {
    try {
      // Add timeout to individual download (90 seconds for large files)
      const timeoutPromise = new Promise<Blob>((_, reject) => {
        setTimeout(() => reject(new Error('Download timeout after 90 seconds')), 90000)
      })

      const downloadPromise = mediaService.downloadDocument(docId)
      const blob = await Promise.race([downloadPromise, timeoutPromise])
      return blob as Blob
    } catch (error) {
      if (i === retries) throw error
      console.log(`🔄 Retry ${i + 1}/${retries} for document ${docId}`)
      notificationService.info(`Retrying download (attempt ${i + 2}/${retries + 1})...`)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  throw new Error('All retries failed')
}

// ─── Generate merged PDF ──────────────────────────────────────────────────────

const generateMergedPDF = async () => {
  if (!canGenerate.value) return

  if (hasSelectedChecklistDocs.value) {
    if (!departmentAddress.value.trim()) {
      departmentAddressError.value = 'Department/Organization Address is required for compliance documents'
      return
    }
    if (!props.tenderData) {
      notificationService.warning('Tender data not available'); return
    }
  }

  generatingPDF.value = true
  const today = new Date().toISOString().split('T')[0]

  // Add timestamp to filename
  const now = new Date();
  const timestamp = `${now.getHours().toString().padStart(2, '0')}-${now.getMinutes().toString().padStart(2, '0')}-${now.getSeconds().toString().padStart(2, '0')}`;

  const tenderRef = props.tenderData
    ? ((props.tenderData.referenceNumber || props.tenderData.referenceNo || 'Tender') as string).replace(/[/\\]/g, '_')
    : 'Tender'
  const docsToAppend = sharedDocuments.value.filter(d =>
    tempSelectedSharedDocIds.value.includes(d.id) && isMergeableFile(d)
  )

  // Reset progress tracking
  downloadProgress.value = {
    current: 0,
    total: docsToAppend.length,
    currentFile: ''
  }

  const filename = `Compliance_Documents_${tenderRef}_${today}_${timestamp}.pdf`

  // Track successfully added document titles
  const successfullyAddedDocTitles: string[] = []

  try {
    const mergedPdf = await PDFDocument.create()
    const totalItems = checkedList.value.length + docsToAppend.length
    let processedItems = 0

    if (hasSelectedChecklistDocs.value) {
      const { default: jsPDF } = await import('jspdf')
      const baseData: TemplateData = {
        companySignatoryName: companyData.value.name,
        companyAddress:       companyData.value.address,
        companyLogo:          companyLogoBase64.value,
        companySignatory:     companySignatureBase64.value,
        organization:         (props.tenderData!.organization || props.tenderData!.departmentName || '') as string,
        departmentAddress:    departmentAddress.value,
        tenderRefNo:          (props.tenderData!.referenceNumber || props.tenderData!.referenceNo || 'N/A') as string,
        date:                 formatDateForPDF(today),
        tenderBrief:          (props.tenderData!.tenderBrief || '') as string,
        locationAddition:     companyData.value.locationAddition,
        minimumLocalContent:  companyData.value.minimumLocalContent,
        signatoryName:        companyData.value.signatoryName,
        designation:          companyData.value.designation,
        signatureDate:        formatDateForPDF(today),
        authorizedPerson:     companyData.value.authorizedPerson,
        position:             companyData.value.positionPower,
        departmentName:       (props.tenderData!.departmentName || props.tenderData!.organization || '') as string,
      }

      for (let i = 0; i < checkedList.value.length; i++) {
        const docName = checkedList.value[i]
        generationStep.value = `Rendering ${i + 1}/${checkedList.value.length}: ${docName}…`
        const html = buildTemplateHtml(docName, baseData)
        if (!html) continue
        const canvas = await renderHtmlToCanvas(html)
        const singlePdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true })
        addCanvasToPdf(singlePdf, canvas, true)
        const bytes = singlePdf.output('arraybuffer')
        const srcDoc = await PDFDocument.load(bytes)
        const pages = await mergedPdf.copyPages(srcDoc, srcDoc.getPageIndices())
        pages.forEach((p: PDFPage) => mergedPdf.addPage(p))
        processedItems++
        generationStep.value = `Processed ${processedItems}/${totalItems} items…`
      }
    }

    if (docsToAppend.length > 0) {
      generationStep.value = `Downloading ${docsToAppend.length} shared documents…`

      // Track successfully and failed downloads
      const successfullyDownloaded: { doc: MediaDocumentItem; blob: Blob; index: number; sizeMB: number }[] = []
      const failedDownloads: { doc: MediaDocumentItem; error: string }[] = []

      // Download files sequentially (not parallel) to avoid overwhelming the server
      for (let idx = 0; idx < docsToAppend.length; idx++) {
        const doc = docsToAppend[idx]
        downloadProgress.value.current = idx + 1
        downloadProgress.value.currentFile = doc.title
        generationStep.value = `Downloading ${idx + 1}/${docsToAppend.length}: ${doc.title}…`

        try {
          // Use retry mechanism for download
          const startTime = Date.now()
          const blob = await downloadWithRetry(doc.id, 2, 3000)
          const downloadTime = ((Date.now() - startTime) / 1000).toFixed(1)
          const sizeMB = blob.size / 1024 / 1024

          // Show warning for very large files
          if (sizeMB > 20) {
            notificationService.info(`"${doc.title}" (${sizeMB.toFixed(2)}MB) may take a moment to process...`)
          }

          console.log(`✅ Downloaded "${doc.title}" in ${downloadTime}s (${sizeMB.toFixed(2)}MB)`)

          successfullyDownloaded.push({
            doc,
            blob,
            index: idx,
            sizeMB
          })
          processedItems++
          generationStep.value = `Processed ${processedItems}/${totalItems} items…`
        } catch (error) {
          console.error(`❌ Failed to download ${doc.title}:`, error)
          const errorMessage = error instanceof Error ? error.message : ''
          failedDownloads.push({ doc, error: errorMessage })

          // Show specific error message
          if (errorMessage.toLowerCase().includes('timeout')) {
            notificationService.warning(`"${doc.title}" download timed out after 90 seconds and was skipped`)
          } else if (errorMessage.toLowerCase().includes('size') || errorMessage.toLowerCase().includes('large')) {
            notificationService.warning(`"${doc.title}" exceeds size limit and was skipped`)
          } else {
            notificationService.warning(`Could not include "${doc.title}" — ${errorMessage || 'download failed'}`)
          }
        }
      }

      // Show summary of failed downloads
      if (failedDownloads.length > 0) {
        console.log(`⚠️ ${failedDownloads.length} file(s) failed to download:`, failedDownloads.map(f => f.doc.title).join(', '))
        notificationService.warning(`${failedDownloads.length} file(s) could not be included in the merged PDF`)
      }

      // Clear progress tracking
      downloadProgress.value = { current: 0, total: 0, currentFile: '' }

      // Process successfully downloaded files and track which ones were added
      for (let idx = 0; idx < successfullyDownloaded.length; idx++) {
        const { doc, blob, sizeMB } = successfullyDownloaded[idx]
        generationStep.value = `Adding ${idx + 1}/${successfullyDownloaded.length}: ${doc.title}…`

        try {
          const arrayBuffer = await blob.arrayBuffer()
          const fileType = (doc.fileType || '').toLowerCase()
          const title = (doc.title || '').toLowerCase()

          // Show processing indicator for large files
          if (sizeMB > 20) {
            notificationService.info(`Processing large file "${doc.title}" (${sizeMB.toFixed(2)}MB)...`)
          }

          let wasAdded = false

          if (fileType.includes('pdf') || title.endsWith('.pdf')) {
            // Handle large PDFs
            try {
              const srcDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true })
              const pageCount = srcDoc.getPageCount()
              console.log(`📄 Adding PDF "${doc.title}" with ${pageCount} pages (${sizeMB.toFixed(2)}MB)`)

              const pages = await mergedPdf.copyPages(srcDoc, srcDoc.getPageIndices())
              pages.forEach((p: PDFPage) => mergedPdf.addPage(p))
              wasAdded = true
            } catch (pdfError) {
              console.error(`Failed to process PDF "${doc.title}":`, pdfError)
              notificationService.warning(`Could not include "${doc.title}" — PDF may be corrupted or password protected`)
            }
          } else if (fileType.includes('jpeg') || fileType.includes('jpg') || title.endsWith('.jpg') || title.endsWith('.jpeg')) {
            const img = await mergedPdf.embedJpg(arrayBuffer)
            const page = mergedPdf.addPage([595.28, 841.89])
            const { width, height } = img.scaleToFit(595.28, 841.89)
            page.drawImage(img, { x: (595.28 - width) / 2, y: (841.89 - height) / 2, width, height })
            wasAdded = true
          } else if (fileType.includes('png') || title.endsWith('.png')) {
            const img = await mergedPdf.embedPng(arrayBuffer)
            const page = mergedPdf.addPage([595.28, 841.89])
            const { width, height } = img.scaleToFit(595.28, 841.89)
            page.drawImage(img, { x: (595.28 - width) / 2, y: (841.89 - height) / 2, width, height })
            wasAdded = true
          } else {
            notificationService.warning(`"${doc.title}" format not supported for merging`)
          }

          // Only add to successfully added list if the document was actually added
          if (wasAdded) {
            successfullyAddedDocTitles.push(doc.title)
          }
        } catch (err) {
          console.error(`Failed to process "${doc.title}":`, err)
          notificationService.warning(`Could not include "${doc.title}" — file may be corrupted or unsupported`)
        }
      }
    }

    generationStep.value = 'Finalizing PDF…'
    const mergedBytes = await mergedPdf.save()
    const mergedBlob = new Blob([new Uint8Array(mergedBytes)], { type: 'application/pdf' })
    const finalSizeMB = mergedBlob.size / 1024 / 1024

    if (mergedBlob.size > MAX_FILE_SIZE) {
      notificationService.error(`Merged PDF is too large (${finalSizeMB.toFixed(2)}MB). Maximum is ${MAX_FILE_SIZE_MB}MB.`)
      return
    }

    generationStep.value = 'Uploading merged PDF…'
    const formData = new FormData()
    formData.append('file', mergedBlob, filename)

    // ✅ IMPORTANT: Only include documents that were successfully added
    formData.append('selectedDocuments', JSON.stringify([
      ...checkedList.value,
      ...successfullyAddedDocTitles
    ]))
    formData.append('selectedSharedDocIds', JSON.stringify(selectedSharedDocIds.value))

    // Reset upload progress
    uploadProgress.value = 0

    // Show upload progress for large files
    if (finalSizeMB > 20) {
      notificationService.info(`Uploading ${finalSizeMB.toFixed(2)}MB file. This may take a moment...`)
    }

    // Always POST — server appends to the file list, never deletes old files
    await api.post(
      `/api/tenders/${props.tenderId}/compliance-file`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            uploadProgress.value = percentCompleted
            generationStep.value = `Uploading merged PDF... ${percentCompleted}%`
          }
        }
      },
    )

    if (successfullyAddedDocTitles.length > 0 || checkedList.value.length > 0) {
      // Update the permanent selectedSharedDocIds with the successfully added documents only
      // Filter tempSelectedSharedDocIds to only include documents that were actually added
      const successfullyAddedDocIds = sharedDocuments.value
        .filter(doc => successfullyAddedDocTitles.includes(doc.title))
        .map(doc => doc.id)

      selectedSharedDocIds.value = [...successfullyAddedDocIds]
      await saveChecklistToDB()
    }

    // Save checklist and reload uploaded file info
    // await saveChecklistToDB()
    await loadUploadedFile()

    // Force Vue to recognize the change
    await nextTick()

    const parts: string[] = []
    if (checkedList.value.length > 0) parts.push(`${checkedList.value.length} compliance doc(s)`)
    if (successfullyAddedDocTitles.length > 0) parts.push(`${successfullyAddedDocTitles.length} shared doc(s)`)
    notificationService.success(`Merged PDF saved (${finalSizeMB.toFixed(2)}MB) — ${parts.join(' + ')}`)

    closeModal()
    emit('checklist-updated')

  } catch (error: unknown) {
    console.error('PDF generation error:', error)
    let errorMessage = 'Failed to generate merged PDF'
    if (error && typeof error === 'object') {
      const axiosError = error as AxiosErrorType
      if (axiosError.response?.status === 413) {
        errorMessage = `File too large for server. Maximum upload size is ${MAX_FILE_SIZE_MB}MB.`
      } else if (axiosError.response?.status === 400) {
        const serverMessage = axiosError.response?.data?.message || axiosError.response?.data?.error
        errorMessage = serverMessage || errorMessage
      } else if (axiosError.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. The file may be too large or the server is busy.'
      } else if (axiosError.message) {
        errorMessage = axiosError.message
      }
    }
    notificationService.error(errorMessage)
  } finally {
    generatingPDF.value = false
    generationStep.value = ''
    downloadProgress.value = { current: 0, total: 0, currentFile: '' }
     uploadProgress.value = 0
  }
}

// const deleteFile = async (index: number) => {
//   const file = uploadedFiles.value[index]
//   if (!file) return
//   if (!confirm(`Delete "${file.originalName || file.filename}"? This cannot be undone.`)) return
//   deletingFileIndex.value = index
//   try {
//     await api.delete(`/api/tenders/${props.tenderId}/compliance-file`, {
//       data: { filename: file.filename }
//     })
//     uploadedFiles.value.splice(index, 1)
//     uploadedFile.value = uploadedFiles.value[0] ?? null
//     notificationService.success('File deleted successfully')
//   } catch (e) {
//     notificationService.error('Failed to delete file')
//     console.error(e)
//   } finally {
//     deletingFileIndex.value = null
//   }
// }

const downloadFile = async (index = 0, retryCount = 0) => {
  const file = uploadedFiles.value[index] ?? uploadedFile.value
  if (!file?.filename) {
    notificationService.error('No file to download');
    return
  }

  // Set the specific file being downloaded
  downloadingFileId.value = file.filename

  const startTime = Date.now()
  const maxRetries = 2
  const timeout = 180000 // 3 minutes timeout for large files

  const preparingTimeout = setTimeout(() => {
    if (downloadingFileId.value === file.filename) {
      notificationService.info(`Downloading "${file.originalName || file.filename}"... This may take a moment for large files.`)
    }
  }, 3000)

  try {
    // Create an AbortController for timeout
    const abortController = new AbortController()
    const timeoutId = setTimeout(() => abortController.abort(), timeout)

    const res = await api.get(`/api/tenders/${props.tenderId}/compliance-file/download`, {
      responseType: 'blob',
      timeout: timeout,
      params: { _: Date.now() },
      signal: abortController.signal
    })

    clearTimeout(timeoutId)

    const elapsed = ((Date.now() - startTime) / 1000).toFixed(1)
    const sizeMB = res.data.size / 1024 / 1024

    const url = window.URL.createObjectURL(new Blob([res.data]))
    const link = document.createElement('a')
    link.href = url
    link.download = file.originalName || file.filename || `Compliance_${props.tenderId}_${Date.now()}.pdf`
    document.body.appendChild(link); link.click()
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url)
    }, 1000)

    notificationService.success(`Downloaded "${file.originalName || file.filename}" (${sizeMB.toFixed(2)}MB) in ${elapsed}s`)
  } catch (error: unknown) {
    console.error('Download error:', error)

    // Retry logic for network errors
    const isNetworkError = error instanceof Error &&
      (error.message.includes('timeout') ||
       error.message.includes('abort') ||
       error.message.includes('network') ||
       ('code' in error && error.code === 'ECONNABORTED'))

    if (isNetworkError && retryCount < maxRetries) {
      notificationService.info(`Retrying download (attempt ${retryCount + 2}/${maxRetries + 1})...`)
      // Wait 2 seconds before retry
      await new Promise(resolve => setTimeout(resolve, 2000))
      await downloadFile(index, retryCount + 1)
      return
    }

    const errorMessage = error instanceof Error ? error.message : 'Failed to download file'
    if (errorMessage.includes('timeout') || errorMessage.includes('abort')) {
      notificationService.error(`Download timed out after ${timeout / 1000} seconds. The file may be too large.`)
    } else {
      notificationService.error(errorMessage)
    }
  } finally {
    clearTimeout(preparingTimeout)
    downloadingFileId.value = null
  }
}

// ─── Formatters ───────────────────────────────────────────────────────────────

const formatFileSize = (size: string | number): string => {
  const n = typeof size === 'string' ? parseInt(size, 10) : size
  if (!n || isNaN(n as number)) return '0 Bytes'
  const k = 1024; const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(n) / Math.log(k))
  return parseFloat((n / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDate = (ds: string): string => {
  if (!ds) return ''
  try {
    const d = new Date(ds)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch { return '' }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(async () => {
  await loadChecklistData()
  loadCompanyData()
  await loadUploadedFile()
})

watch(() => props.tenderId, async () => {
  if (props.tenderId) {
    await loadChecklistData()
    await loadUploadedFile()
  }
})

// watch(showModal, async (newVal, oldVal) => {
//   if (oldVal === true && newVal === false) {
//     console.log('💾 Saving on modal close. sharedDocs:', selectedSharedDocIds.value)
//     await saveChecklistToDB()
//   }
// })
</script>

<template>
  <CardComponent
    title="Compliance Documents"
    :showHeaderBorder="true"
    :showHeaderPaddingBottom="true"
    bodyClass="grid-cols-1"
  >
    <template #header>
      <BaseButton
        v-if="isAgent && !isTenderWonOrLost"
        :icon="EditIcon"
        text="Manage"
        size="xs"
        variant="secondary"
        @click="openModal"
      />
    </template>

    <template #body>
      <LoaderDefault v-if="loading || loadingFile" title="Loading..." />

      <div v-else class="flex flex-col gap-3 w-full">

        <!-- ── File list (newest first) ── -->
        <div v-if="uploadedFiles && uploadedFiles.length > 0" class="flex flex-col gap-2">
          <p class="text-xs font-medium text-gray-500">
            Compliance Files
            <span class="ml-1 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
              {{ uploadedFiles.length }}
            </span>
          </p>

          <div
            v-for="(file, index) in uploadedFiles"
            :key="file.filename"
            class="p-3 bg-blue-50 border border-blue-200 rounded-md"
          >
            <!-- Top row: icon + name + badge + actions -->
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-2 min-w-0">
                <FilePdfIcon class="w-7 h-7 text-blue-600 shrink-0" />
                <div class="min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span
                      class="text-sm font-medium text-gray-900 truncate"
                      :title="file.originalName || file.filename"
                    >
                      {{ file.originalName || file.filename }}
                    </span>
                    <span
                      v-if="index === 0"
                      class="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium shrink-0"
                    >Latest</span>
                  </div>
                  <!-- Meta: count · date · size -->
                  <p class="text-xs text-gray-500 mt-0.5">
                    {{ file.selectedDocuments?.length || 0 }} doc(s)
                    <template v-if="file.uploadedAt">
                      · {{ formatDate(file.uploadedAt) }}
                    </template>
                    <template v-if="file.size">
                      · {{ (file.size / 1024 / 1024).toFixed(2) }} MB
                    </template>
                  </p>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center gap-2 shrink-0">
                <BaseButton
                  @click="downloadFile(index)"
                  :icon="DownloadIcon"
                  size="xs"
                  variant="primary"
                  :loading="downloadingFileId === file.filename"
                  :disabled="downloadingFileId !== null"
                  tooltip="Download"
                />
                <BaseButton
                  v-if="isAgent && !isTenderWonOrLost"
                  @click="confirmDeleteFile(index)"
                  :icon="DeleteIcon"
                  size="xs"
                  variant="danger"
                  :loading="deletingFileIndex === index"
                  :disabled="!!(downloadingFileId || deletingFileIndex !== null)"
                  tooltip="Delete"
                />
              </div>
            </div>

            <!-- Doc tags — show only when there are selectedDocuments -->
            <div
              v-if="file.selectedDocuments && file.selectedDocuments.length > 0"
              class="mt-2 flex flex-wrap gap-1"
            >
              <span
                v-for="doc in file.selectedDocuments"
                :key="doc"
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-white border border-blue-200 text-blue-700 truncate"
                :title="doc"
              >
                <span class="text-green-500 shrink-0">✓</span>{{ doc }}
              </span>
            </div>
          </div>
        </div>

        <!-- Empty state — shown only when no files at all -->
        <EmptyState
          v-else
          :icon="DocsIcon"
          title="No compliance documents"
          :description="isAgent && !isTenderWonOrLost ? 'Click Manage to generate documents' : ''"
        />

      </div>
    </template>
  </CardComponent>

  <ModalDefault
    v-if="isAgent && !isTenderWonOrLost"
    v-model="showModal"
    title="Generate Merged Compliance PDF"
    width="max-w-4xl"
    classBody="my-4"
  >
    <div class="mb-5">
      <GlobalFormMessage
        v-if="generationStep"
        :message="generationStep"
        type="loading"
        :show-icon="true"
        :dismissible="false"
        :auto-dismiss="false"
      />

      <!-- Progress bar for large file downloads -->
      <div v-if="downloadProgress.total > 0" class="mt-2 mb-3">
        <div class="flex justify-between text-xs text-gray-600 mb-1">
          <span class="truncate max-w-[200px]">{{ downloadProgress.currentFile }}</span>
          <span>{{ downloadProgress.current }}/{{ downloadProgress.total }}</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            class="bg-blue-600 h-2 rounded-full transition-all duration-300"
            :style="{ width: `${(downloadProgress.current / downloadProgress.total) * 100}%` }"
          ></div>
        </div>
        <p class="text-xs text-gray-400 mt-1">Downloading shared documents...</p>
      </div>

      <!-- Progress bar for upload -->
      <div v-if="uploadProgress > 0 && uploadProgress < 100" class="mt-2 mb-3">
        <div class="flex justify-between text-xs text-gray-600 mb-1">
          <span>Uploading merged PDF...</span>
          <span>{{ uploadProgress }}%</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            class="bg-green-600 h-2 rounded-full transition-all duration-300"
            :style="{ width: `${uploadProgress}%` }"
          ></div>
        </div>
        <p class="text-xs text-gray-400 mt-1">Please wait while your document is being saved...</p>
      </div>

      <div
        v-else-if="canGenerate"
        class="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-xs text-gray-600"
      >
        <MergeIcon class="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span>
          <span class="font-semibold text-gray-800">Output:</span>
          One merged PDF containing
          <span v-if="selectedChecklistDocs.length > 0" class="text-blue-600 font-medium">
            {{ selectedChecklistDocs.length }} compliance page(s)
          </span>
          <template v-if="selectedChecklistDocs.length > 0 && mergeableSelectedDocs.length > 0"> + </template>
          <span v-if="mergeableSelectedDocs.length > 0" class="text-purple-600 font-medium">
            {{ mergeableSelectedDocs.length }} shared document(s)
          </span>
          — uploaded &amp; saved to this tender.
        </span>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-6 min-h-0">
      <!-- LEFT: Compliance Checklist -->
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-800">Compliance Documents</h3>
          <span class="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
            {{ selectedChecklistDocs.length }} / {{ checklistDocuments.length }} selected
          </span>
        </div>

        <div class="space-y-1">
          <label class="block text-xs font-medium text-gray-700">
            Department/Organization Address
            <span v-if="hasSelectedChecklistDocs" class="text-red-500 ml-0.5">*</span>
          </label>
          <TextAreaDefault
            id="departmentAddress"
            name="departmentAddress"
            v-model="departmentAddress"
            placeholder="Enter department/organization address"
            :errorMessage="departmentAddressError"
            :rows="3"
            :disabled="generatingPDF || !hasSelectedChecklistDocs"
          />
          <p class="text-xs text-gray-400">Required only when compliance items are ticked</p>
        </div>

        <div class="flex-1 overflow-y-auto space-y-1 max-h-64 pr-1 scrollbar-thin">
          <div
            v-for="doc in checklistDocuments"
            :key="doc"
            class="flex items-center gap-2 px-2 py-2 rounded transition-colors"
            :class="checkedTemplates[doc] ? 'bg-green-50' : 'hover:bg-gray-50'"
          >
            <CheckBoxDefault
              :id="`chk-${doc}`"
              v-model="checkedTemplates[doc]"
              size="sm"
              :disabled="generatingPDF"
            />
            <label :for="`chk-${doc}`" class="text-sm text-gray-700 cursor-pointer flex-1 select-none">
              {{ doc }}
            </label>
          </div>
        </div>
      </div>

      <!-- RIGHT: Shared Documents -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-800">Shared Documents</h3>
          <span class="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
            {{ mergeableSelectedDocs.length }} selected
          </span>
        </div>

        <div class="flex items-center gap-2">
          <TextInputDefault
            id="search-shared-docs"
            v-model="searchFilter"
            @input="handleSearch"
            type="text"
            placeholder="Search documents…"
            size="xs"
            class="flex-1"
          />
          <BaseButton v-if="searchFilter" text="Clear" size="xs" variant="ghost" @click="clearSearch" />
        </div>

        <div v-if="hasUnmergeableFiles" class="bg-orange-50 border border-orange-200 rounded-lg p-2">
          <div class="flex items-start gap-1.5">
            <svg class="h-3.5 w-3.5 text-orange-400 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <p class="text-xs text-orange-700">
              <span class="font-medium">Skipped (non-mergeable):</span> {{ unmergeableFileNames }}
            </p>
          </div>
        </div>

        <LoaderDefault v-if="docsLoading" text="Loading documents..." />

        <div v-else-if="docsError" class="flex flex-col items-center py-6 gap-2">
          <p class="text-xs text-gray-500">{{ docsError }}</p>
          <BaseButton text="Retry" size="xs" variant="primary" @click="fetchSharedDocuments" />
        </div>

        <EmptyState
          v-else-if="sharedDocuments.length === 0"
          :icon="FileWaveIcon"
          :title="searchFilter ? 'No documents found' : 'No documents available'"
        />

        <div v-else class="flex flex-col gap-2 flex-1 min-h-0">
          <!-- <div class="flex items-center gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-md">
            <CheckBoxDefault v-model="allSharedSelected" size="sm" :disabled="generatingPDF" />
            <span class="text-xs text-gray-600">Select All</span>
            <span class="text-xs text-gray-400 ml-auto">PDF &amp; images only</span>
          </div> -->

          <div class="overflow-y-auto space-y-1.5 max-h-[17rem] pr-1 scrollbar-thin">
            <div
              v-for="doc in filteredSharedDocuments"
              :key="doc.id"
              class="flex items-center gap-2 p-2.5 border rounded-md transition-colors"
              :class="{
                'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800': doc.selected,
                'border-gray-200 hover:bg-gray-50 dark:border-gray-800': !doc.selected,
                'opacity-50': !isMergeableFile(doc),
              }"
            >
              <CheckBoxDefault
                :modelValue="doc.selected || false"
                :disabled="generatingPDF || !isMergeableFile(doc)"
                @update:modelValue="(v: boolean) => handleSharedDocCheckbox(doc, v)"
                size="sm"
              />
              <div class="flex-shrink-0 w-7 h-7 rounded flex items-center justify-center bg-gray-100 dark:bg-white/5">
                <component :is="getDocumentIcon(doc)" class="w-4 h-4" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5 mb-0.5">
                  <span class="text-xs font-medium truncate">{{ doc.title }}</span>
                  <span v-if="isMergeableFile(doc)" class="flex-shrink-0 text-xs px-1 py-0.5 rounded-full bg-green-100 text-green-700">✓</span>
                  <span v-else-if="isOfficeDocument(doc)" class="flex-shrink-0 text-xs px-1 py-0.5 rounded-full bg-purple-100 text-purple-700">Office</span>
                  <span v-else class="flex-shrink-0 text-xs px-1 py-0.5 rounded-full bg-gray-100 text-gray-600">Other</span>
                </div>
                <p class="text-xs text-gray-400">{{ formatFileSize(doc.fileSize) }} · {{ formatDate(doc.uploadedAt) }}</p>
              </div>
              <BaseButton
                size="xs" variant="secondary" :icon="EyeIcon"
                tooltip="View document" class="flex-shrink-0"
                @click="viewDocument(doc)"
              />
            </div>
          </div>

          <p class="text-xs text-gray-400 pt-0.5">{{ sharedDocuments.length }} documents · mergeable: PDF, JPG, PNG, GIF, BMP, WEBP, TIFF</p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Cancel" variant="secondary" @click="closeModal" :disabled="generatingPDF" />
        <BaseButton
          @click="generateMergedPDF"
          :disabled="!canGenerate"
          :text="generateButtonText"
          variant="primary"
          :loading="generatingPDF"
          :icon="MergeIcon"
        />
      </div>
    </template>
  </ModalDefault>

  <!-- Delete Confirmation Modal -->
<!-- Delete Confirmation Modal -->
<ModalDefault
  v-model="showDeleteModal"
  title="Delete Document"
  width="max-w-md"
  classBody="my-4"
  @close="closeDeleteModal"
  :loading="deletingDocument"
>
  <div class="space-y-4">
    <div class="bg-red-50 border-l-4 border-red-400 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-red-700">
            <span class="font-medium">Warning:</span> Are you sure you want to delete this document? This action cannot be undone.
          </p>
        </div>
      </div>
    </div>

    <div v-if="documentToDelete" class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-sm font-medium text-gray-700 mb-2">Document Details:</h3>
      <div class="space-y-3">
        <div class="flex items-center gap-3">
          <FilePdfIcon class="w-12 h-12 text-blue-600" />
          <div class="min-w-0">
            <p class="font-medium text-gray-900 break-words">{{ documentToDelete.originalName || documentToDelete.filename }}</p>
            <p class="text-xs text-gray-500 mt-1">
              {{ documentToDelete.selectedDocuments?.length || 0 }} document(s) included
            </p>
          </div>
        </div>
        <div class="text-sm">
          <span class="text-gray-600">Size:</span>
          <span class="font-medium ml-2">{{ formatFileSize(documentToDelete.size || 0) }}</span>
        </div>
        <div class="text-sm">
          <span class="text-gray-600">Uploaded:</span>
          <span class="font-medium ml-2">{{ formatDate(documentToDelete.uploadedAt || '') }}</span>
        </div>
      </div>
    </div>
  </div>

  <template #footer>
    <div class="flex justify-end gap-3">
      <BaseButton
        text="Cancel"
        variant="secondary"
        @click="closeDeleteModal"
        :disabled="deletingDocument"
      />
      <BaseButton
        text="Delete Document"
        variant="danger"
        :loading="deletingDocument"
        loading-text="Deleting..."
        @click="handleDeleteSubmit"
      />
    </div>
  </template>
</ModalDefault>
</template>

<style scoped>
.grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #cbd5e0 #f1f5f9; }
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 4px; }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
